import { Divider } from 'antd';
import { PriceTotalsReturn } from 'pages/easy-estimator/components/price/price.utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { rowType } from '@common/enums/estimate-section.enums';
import PromotionDateLabel from '@components/labels/promotion-date-label/promotion-date-label';
import TaxStatement from '@components/labels/tax-statement/tax-statement';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { resetDiscountOverride, selectDiscountOverride, setDiscountOverride } from '@redux/slices/estimateSlice';

import { ContentTypeEnum, TotalPricingRowItem } from '../line-item-helper';

const decimalPattern = /^\d*\.?\d{0,2}$/;
const finalDecimalPattern = /^\d+(\.\d{1,2})?$/;

export const ProductSummary = ({
    deliveryCost,
    totals,
    showMinQtyWarning,
    promotionEndDate,
}: {
    deliveryCost?: number;
    totals: PriceTotalsReturn;
    showMinQtyWarning: boolean;
    promotionEndDate?: string;
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [discountOverrideLocal, setDiscountOverrideLocal] = useState<string | undefined>(undefined);

    const [showDiscountOverrideInput, setShowDiscountOverrideInput] = useState<boolean>(false);
    const discountOverrideAmount = useAppSelector(selectDiscountOverride);

    const { userOptions } = useRetailerSettings();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const isInstalled = selectedMaterial?.isInstalled ?? true;

    const subTotal = totals.priceWithoutDiscount;
    const savings = subTotal - totals.priceWithDiscount;
    const installationCost = 0; //zero by default, exist for possible implementation of non included costs
    const total = Math.max(
        subTotal - savings - (discountOverrideAmount ?? 0) + (installationCost ?? 0) + (deliveryCost ?? 0),
        0
    );

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.currentTarget.blur();
        }
    };

    return (
        <div id="total pricing" className="totals">
            <TotalPricingRowItem
                name={t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.SUBTOTAL')}
                value={subTotal}
                rowStyle={rowType.defaultStyle}
            />
            {savings ? (
                <TotalPricingRowItem
                    name={t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.SAVINGS')}
                    value={savings}
                    showZeroPrice
                    rowStyle={rowType.savingsStyle}
                />
            ) : null}

            {userOptions.discountOverride && !showDiscountOverrideInput ? (
                <TotalPricingRowItem
                    tooltipProps={{
                        title: t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.ADDITIONAL_DISCOUNT_TOOLTIP'),
                        color: 'white',
                        overlayInnerStyle: { color: 'black' },
                    }}
                    onClick={() => setShowDiscountOverrideInput(true)}
                    className="discount-override-subtotal-container discount-override-subtotal-container--next-to-input"
                    name={t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.ADDITIONAL_DISCOUNT')}
                    value={discountOverrideAmount}
                    rowStyle={rowType.savingsStyle}
                    showZeroPrice
                />
            ) : null}
            {userOptions.discountOverride && showDiscountOverrideInput ? (
                <TotalPricingRowItem
                    value={undefined}
                    contentType={ContentTypeEnum.INPUT}
                    inputProps={{
                        value: discountOverrideLocal ?? String(discountOverrideAmount ?? ''),
                        onChange: (e) => {
                            if (decimalPattern.test(e.target.value)) {
                                setDiscountOverrideLocal(e.target.value);
                            }
                        },
                        onBlur: () => {
                            if (!discountOverrideLocal || !finalDecimalPattern.test(discountOverrideLocal)) {
                                dispatch(resetDiscountOverride());
                            } else {
                                dispatch(setDiscountOverride(Math.min(Number(discountOverrideLocal), subTotal)));
                            }
                            setShowDiscountOverrideInput(false);
                        },
                        onKeyDown: handleKeyDown,
                    }}
                    className="discount-override-input-container"
                    name={t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.ADDITIONAL_DISCOUNT')}
                    rowStyle={rowType.savingsStyle}
                />
            ) : null}

            <TotalPricingRowItem
                name={t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.DELIVERY')}
                value={deliveryCost}
                rowStyle={rowType.defaultStyle}
            />
            {isInstalled ? (
                <TotalPricingRowItem
                    name={t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.INSTALLATION')}
                    value={installationCost}
                    rowStyle={rowType.defaultStyle}
                />
            ) : null}
            <Divider className="divider-large" />
            <div style={{ display: 'flex', justifyContent: 'end' }}>
                <TotalPricingRowItem
                    name={`${t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.TOTAL')}`}
                    value={total}
                    showZeroPrice
                    rowStyle={rowType.totalStyle}
                />
                <div>{`${showMinQtyWarning ? '*' : ''}`}</div>
            </div>

            <TaxStatement label={'ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.TAX'} />
            {promotionEndDate ? (
                <PromotionDateLabel expirationDate={promotionEndDate} align="right" fontSize="small" />
            ) : null}
        </div>
    );
};
